// Explorer Evaluation Service - Risk prioritization for Explorer assessments
// Uses Explorer-specific Redux state (explorerRisks, not modelRisks)
import {
  updateExplorerRiskPriority
} from '../../redux/slices/risksSlice';
import { authFetch } from '../../utils/authFetch';
import {
  setExplorerRiskEvaluation,
  saveExplorerEvaluation
} from '../../redux/slices/riskEvaluationsSlice';

export class ExplorerEvaluationService {
  constructor(dispatch, modelId, assessmentUuid, contextData = {}, impactCategories = [], liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.contextData = contextData;
    this.impactCategories = impactCategories; // Template-based impact categories
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeEvaluation(areaKey, areaDefinition, riskFormTaxonomy, inheritedRisks, progressCallback) {
    try {
      if (progressCallback) progressCallback('evaluation');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Access Explorer risks (not modelRisks)
      const explorerRisks = currentState.riskItems?.byAssessment?.[this.assessmentUuid]?.explorerRisks || {};
      const areaRisks = explorerRisks[areaKey]?.areaRisks || {};

      console.log(`[ExplorerEvaluationService] State source for area: ${areaKey}`);
      console.log(`[ExplorerEvaluationService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[ExplorerEvaluationService] Available risk categories:`, Object.keys(areaRisks));

      // Find risks that need evaluation
      const risksToEvaluate = [];

      Object.entries(areaRisks).forEach(([category, riskArray]) => {
        console.log(`[ExplorerEvaluationService] Checking category "${category}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[ExplorerEvaluationService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && !risk.priority) {
              console.log(`[ExplorerEvaluationService] FOUND unevaluated risk: "${risk.riskName}"`);
              risksToEvaluate.push({
                ...risk,
                areaKey,
                category
              });
            }
          });
        }
      });

      if (risksToEvaluate.length > 0) {
        console.log(`[ExplorerEvaluationService] Evaluating ${risksToEvaluate.length} risks using Explorer FastTrack approach`);

        let evaluatedCount = 0;
        for (const risk of risksToEvaluate) {
          try {
            // Get causes data for this risk
            const sourceCauses = this.extractCausesForRisk(risk, areaDefinition);

            console.log(`[ExplorerEvaluationService] Causes for ${risk.riskName}:`, {
              causesCount: sourceCauses?.length || 0
            });

            // Call Explorer-specific FastTrack evaluation
            // NOTE: Backend expects specific field names - see explorerPrioritizeRisks.js
            const evalResponse = await authFetch('/.netlify/functions/explorerPrioritizeRisks', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                riskName: risk.riskName,
                areaName: areaDefinition?.label || areaKey,
                evaluationData: {
                  causes: sourceCauses,
                  category: risk.category,
                  rationale: risk.rationale,
                  confidence: risk.confidence
                },
                assessmentUuid: this.assessmentUuid,
                parentAssessmentUuid: this.contextData?.parentAssessmentUuid,
                businessContext: this.contextData?.businessContext,
                stakeholders: this.contextData?.stakeholders || []
              })
            });

            if (evalResponse.ok) {
              const evalData = await evalResponse.json();
              if (evalData.fastTrackEvaluation) {
                const evaluation = evalData.fastTrackEvaluation;
                const priority = evaluation.priority?.toLowerCase() || 'moderate';
                const rationale = evaluation.rationale || '';

                console.log(`[ExplorerEvaluationService] FastTrack evaluated ${risk.riskName}: ${priority}`);

                // Save evaluation to Explorer state
                await this.saveExplorerEvaluation(risk, evaluation, priority, rationale, areaKey);

                evaluatedCount++;

                // LIVE UPDATE: Stream each risk evaluation as it completes
                if (this.liveUpdateCallback) {
                  this.liveUpdateCallback('riskEvaluation', {
                    riskName: risk.riskName,
                    priority: priority,
                    rationale: rationale,
                    confidence: evaluation.confidence,
                    themes: evaluation.fastTrackData?.selectedThemes?.length || 0,
                    evaluatedCount,
                    totalRisks: risksToEvaluate.length,
                    areaKey
                  });
                }
              }
            } else {
              console.error(`[ExplorerEvaluationService] FastTrack evaluation failed for ${risk.riskName}:`, await evalResponse.text());
            }
          } catch (evalError) {
            console.error(`[ExplorerEvaluationService] Risk evaluation failed for ${risk.riskName}:`, evalError);
          }
        }
      }

      return {
        evaluatedRisks: risksToEvaluate.length,
        areaKey
      };

    } catch (error) {
      console.error('[ExplorerEvaluationService] Evaluation failed:', error);
      throw error;
    }
  }

  // Extract causes data for a specific risk
  extractCausesForRisk(risk, areaDefinition) {
    try {
      // Try from area definition riskSources
      const fromRiskSources = areaDefinition?.riskSources?.find(src => src.name === risk.category)?.causes;
      if (fromRiskSources && Object.keys(fromRiskSources).length > 0) {
        console.log(`[ExplorerEvaluationService] Found causes from riskSources for: ${risk.category}`);
        return Object.entries(fromRiskSources).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }

      console.log(`[ExplorerEvaluationService] No causes found for risk category: ${risk.category}`);
      return [];
    } catch (error) {
      console.error('[ExplorerEvaluationService] Error extracting causes:', error);
      return [];
    }
  }

  // Save Explorer evaluation results using RTK thunk
  async saveExplorerEvaluation(risk, evaluation, priority, rationale, areaKey) {
    try {
      // STEP 1: Save evaluations to DB using RTK thunk
      const themes = evaluation.fastTrackData?.selectedThemes || [];

      if (themes.length > 0) {
        // Save each theme evaluation via RTK thunk
        const savePromises = themes.map(theme =>
          this.dispatch(saveExplorerEvaluation({
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskName: risk.riskName,
            riskId: risk.riskId,
            themeId: theme.themeId,
            riskArea: areaKey,
            priority: priority,
            rationale: rationale,
            data: theme.evaluation?.impact || theme.highLevelAssessment || {}
          })).unwrap().catch(err => {
            console.error(`[ExplorerEvaluationService] Failed to save theme ${theme.themeId} for ${risk.riskName}:`, err);
            return { error: true };
          })
        );

        const results = await Promise.all(savePromises);
        const failedSaves = results.filter(r => r.error);

        if (failedSaves.length > 0) {
          console.error(`[ExplorerEvaluationService] Failed to save ${failedSaves.length}/${themes.length} theme evaluations for ${risk.riskName}`);
        } else {
          console.log(`[ExplorerEvaluationService] Successfully saved ${themes.length} theme evaluations for ${risk.riskName}`);
        }
      } else {
        // No themes - save a single evaluation record with default theme via RTK thunk
        console.log(`[ExplorerEvaluationService] No themes for ${risk.riskName}, saving with default theme`);
        try {
          await this.dispatch(saveExplorerEvaluation({
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskName: risk.riskName,
            riskId: risk.riskId,
            themeId: 1, // Default theme ID for Explorer evaluations without themes
            riskArea: areaKey,
            priority: priority,
            rationale: rationale,
            data: {}
          })).unwrap();
          console.log(`[ExplorerEvaluationService] Default evaluation saved for ${risk.riskName}`);
        } catch (saveError) {
          console.error(`[ExplorerEvaluationService] Failed to save default evaluation for ${risk.riskName}:`, saveError);
        }
      }

      // STEP 2: Update Explorer Redux state with priority
      this.dispatch(
        updateExplorerRiskPriority({
          assessmentUuid: this.assessmentUuid,
          areaKey,
          category: risk.category,
          riskId: risk.riskId,
          priority,
          rationale
        })
      );

      // STEP 3: Store evaluation in Explorer evaluations slice
      this.dispatch(
        setExplorerRiskEvaluation({
          assessmentUuid: this.assessmentUuid,
          areaKey,
          category: risk.category,
          riskId: risk.riskId,
          evaluation: {
            priority,
            rationale,
            confidence: evaluation.confidence,
            fastTrackData: evaluation.fastTrackData,
            evaluatedAt: new Date().toISOString()
          }
        })
      );

      console.log(`[ExplorerEvaluationService] Priority saved for ${risk.riskName}: ${priority}`);

    } catch (error) {
      console.error(`[ExplorerEvaluationService] Error saving Explorer evaluation for ${risk.riskName}:`, error);
    }
  }
}
